import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Paper,
  Collapse,
  TextField,
  Skeleton,
} from "@mui/material";
import {
  Mail,
  ExternalLink,
  Send,
  ChevronDown,
  ChevronRight,
  MessageSquare,
  Zap,
  Check,
} from "react-feather";
import axios from "../../utils/axios";
import { config } from "../../constants";
import { useSMSUnread } from "../../contexts/SMSUnreadContext";

const timeAgo = (dateStr) => {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const InboxPage = () => {
  const { markCustomerRead } = useSMSUnread();
  const [grantStatus, setGrantStatus] = useState("loading");
  const [connecting, setConnecting] = useState(false);

  // Inbox state
  const [conversations, setConversations] = useState([]);
  const [loadingConvos, setLoadingConvos] = useState(false);
  const [expandedThreadId, setExpandedThreadId] = useState(null);
  const [threadMessages, setThreadMessages] = useState({});
  const [loadingThread, setLoadingThread] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [readThreads, setReadThreads] = useState(new Set());
  const [generatingReply, setGeneratingReply] = useState(false);
  const [justSentReply, setJustSentReply] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [generatingPhrase, setGeneratingPhrase] = useState("");
  const typingRef = useRef(null);

  // Fire grant check and conversations fetch in parallel
  useEffect(() => {
    setLoadingConvos(true);
    const grantReq = axios.get(`${config.url.SMS_URL}/api/sms/nylas-grant`);
    const convosReq = axios.get(`${config.url.SMS_URL}/api/sms/inbox/conversations`, { params: { limit: 20, offset: 0 } });

    Promise.all([grantReq, convosReq])
      .then(([grantRes, convosRes]) => {
        if (grantRes.data.grant_id) {
          setGrantStatus("connected");
          setConversations(convosRes.data.conversations || []);
          setHasMore(convosRes.data.has_more || false);
        } else {
          setGrantStatus("disconnected");
        }
      })
      .catch(() => setGrantStatus("disconnected"))
      .finally(() => setLoadingConvos(false));
  }, []);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await axios.get(`${config.url.SMS_URL}/api/sms/inbox/conversations`, {
        params: { limit: 20, offset: conversations.length },
      });
      setConversations((prev) => [...prev, ...(res.data.conversations || [])]);
      setHasMore(res.data.has_more || false);
    } catch {
      // silent fail
    } finally {
      setLoadingMore(false);
    }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await axios.get(`${config.url.SMS_URL}/api/sms/nylas/auth`, {
        params: { base_url: config.url.SMS_URL },
      });
      if (res.data.auth_url) {
        window.location.href = res.data.auth_url;
      }
    } catch {
      setConnecting(false);
    }
  };

  const handleExpand = async (threadId) => {
    if (expandedThreadId === threadId) {
      setExpandedThreadId(null);
      return;
    }
    setExpandedThreadId(threadId);
    setReplyText("");
    setReadThreads((prev) => new Set(prev).add(threadId));
    axios.post(`${config.url.SMS_URL}/api/sms/conversations/mark-read`, { thread_id: threadId }).catch(() => {});

    // Mark message notifications read in sidebar context
    const convo = conversations.find((c) => c.nylas_thread_id === threadId);
    if (convo?.company_name) {
      markCustomerRead(convo.company_name);
    }

    setLoadingThread(threadId);
    try {
      const res = await axios.get(
        `${config.url.SMS_URL}/api/sms/conversations/thread/${encodeURIComponent(threadId)}?skip_sync=true`
      );
      setThreadMessages((prev) => ({ ...prev, [threadId]: res.data.messages || [] }));
    } catch {
      // silent fail
    } finally {
      setLoadingThread(null);
    }
  };

  const handleReply = async (threadId) => {
    if (!replyText.trim()) return;
    setSending(true);

    const msgs = threadMessages[threadId] || [];
    const received = [...msgs].reverse().find((m) => m.status === "received");
    const original = msgs.find((m) => m.status === "sent");
    const toEmail = received?.sent_by || original?.email || "";
    const toName = received?.customer_name || original?.customer_name || "";
    const isSms = threadId.startsWith("sms_");

    try {
      await axios.post(`${config.url.SMS_URL}/api/sms/conversations/reply`, {
        thread_id: threadId,
        body: isSms ? replyText : `<p>${replyText}</p>`,
        to_email: toEmail,
        to_name: toName,
      });

      const newMsg = {
        id: Date.now(),
        message: isSms ? replyText : `<p>${replyText}</p>`,
        subject: isSms ? null : `Re: ${original?.subject || "Follow up"}`,
        status: "sent",
        channel: isSms ? "sms" : "email",
        sent_by: "You",
        sent_at: new Date().toISOString(),
      };
      setThreadMessages((prev) => ({
        ...prev,
        [threadId]: [...(prev[threadId] || []), newMsg],
      }));
      setReplyText("");
      setJustSentReply(true);
      setTimeout(() => setJustSentReply(false), 2000);
    } catch {
      toast.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const handleGenerateReply = async (threadId) => {
    setGeneratingReply(true);
    const isSms = threadId.startsWith("sms_");
    const convo = conversations.find((c) => c.nylas_thread_id === threadId);
    const customerName = convo?.company_name || convo?.subject || "customer";
    const phrase = `Generating ${isSms ? "a SMS" : "an email"} for ${customerName}...`;

    // Start typing animation
    let i = 0;
    setGeneratingPhrase("");
    clearInterval(typingRef.current);
    typingRef.current = setInterval(() => {
      i++;
      setGeneratingPhrase(phrase.slice(0, i));
      if (i >= phrase.length) clearInterval(typingRef.current);
    }, 65);

    try {
      const msgs = threadMessages[threadId] || [];
      const lastReceived = [...msgs].reverse().find((m) => m.status === "received");
      const context = lastReceived
        ? `Reply to: "${lastReceived.message?.replace(/<[^>]*>/g, "").slice(0, 300)}"`
        : "follow up";
      const res = await axios.post(`${config.url.SMS_URL}/api/sms/draft-content`, {
        customer_name: convo?.company_name || "",
        context,
        channel: isSms ? "sms" : "email",
        is_reply: true,
      });
      if (isSms && res.data.sms_message) {
        setReplyText(res.data.sms_message);
      } else if (res.data.body_html) {
        const tmp = document.createElement("div");
        tmp.innerHTML = res.data.body_html;
        setReplyText(tmp.textContent || tmp.innerText || "");
      }
    } catch {
      toast.error("Failed to generate reply");
    } finally {
      clearInterval(typingRef.current);
      setGeneratingPhrase("");
      setGeneratingReply(false);
    }
  };

  const renderConversationList = () => {
    if (loadingConvos) {
      return (
        <Box sx={{ px: 3, py: 2 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Paper key={i} variant="outlined" sx={{ mb: 1, borderRadius: 1.5, px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
              <Skeleton variant="circular" width={14} height={14} />
              <Skeleton variant="circular" width={14} height={14} />
              <Skeleton variant="rounded" width={90} height={18} sx={{ borderRadius: 2 }} />
              <Skeleton variant="text" width="40%" height={20} />
              <Box sx={{ flex: 1 }} />
              <Skeleton variant="text" width={50} height={16} />
            </Paper>
          ))}
        </Box>
      );
    }

    if (conversations.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ py: 6, textAlign: "center" }}>
          No conversations yet.
        </Typography>
      );
    }

    return (
      <Box sx={{ px: 3, py: 2 }}>
        {conversations.map((convo) => {
          const isExpanded = expandedThreadId === convo.nylas_thread_id;
          const msgs = threadMessages[convo.nylas_thread_id] || [];
          const isLoadingThis = loadingThread === convo.nylas_thread_id;
          const isEmail = convo.channel === "email";
          const isUnread = convo.unread_count > 0 && !readThreads.has(convo.nylas_thread_id);

          return (
            <Paper
              key={convo.nylas_thread_id}
              variant="outlined"
              sx={{ mb: 1, borderRadius: 1.5, overflow: "hidden" }}
            >
              {/* Thread header */}
              <Box
                onClick={() => handleExpand(convo.nylas_thread_id)}
                sx={{
                  px: 2,
                  py: 1.5,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  "&:hover": { bgcolor: "action.hover" },
                }}
              >
                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                {isUnread && (
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#1976d2", flexShrink: 0 }} />
                )}
                {isEmail ? (
                  <Mail size={14} style={{ flexShrink: 0, color: "#1976d2" }} />
                ) : (
                  <MessageSquare size={14} style={{ flexShrink: 0, color: "#2e7d32" }} />
                )}
                {convo.company_name && (
                  <Chip
                    label={convo.company_name}
                    size="small"
                    sx={{
                      height: 18,
                      fontSize: "0.6rem",
                      bgcolor: "rgba(0,0,0,0.06)",
                      color: "text.secondary",
                      fontWeight: 600,
                      flexShrink: 0,
                      maxWidth: 140,
                      "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
                    }}
                  />
                )}
                {convo.person_name && (
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem", flexShrink: 0 }}>
                    {convo.person_name}{convo.contact_email ? ` <${convo.contact_email}>` : ""}
                  </Typography>
                )}
                <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: isUnread ? 700 : 600,
                      fontSize: "0.8rem",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {convo.subject || "Untitled"}
                  </Typography>
                </Box>
                {convo.reply_count > 0 && (
                  <Chip
                    label={`${convo.reply_count} repl${convo.reply_count === 1 ? "y" : "ies"}`}
                    size="small"
                    sx={{ height: 20, fontSize: "0.65rem" }}
                  />
                )}
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.7rem", flexShrink: 0 }}>
                  {timeAgo(convo.last_activity)}
                </Typography>
              </Box>

              {/* Expanded thread */}
              <Collapse in={isExpanded}>
                <Box sx={{ px: 2, pb: 2, borderTop: "1px solid", borderColor: "divider" }}>
                  {isLoadingThis ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : (
                    <>
                      {(() => {
                        const isEmailThread = msgs.length > 0 && msgs[0]?.channel === "email";
                        if (isEmailThread) {
                          const reversed = [...msgs].reverse();
                          return (
                            <>
                              {/* Reply area on top */}
                              <TextField
                                size="small"
                                multiline
                                minRows={3}
                                maxRows={8}
                                placeholder={generatingReply ? (generatingPhrase + "\u258D") : "Write your reply..."}
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                fullWidth
                                variant="standard"
                                InputProps={{ disableUnderline: true, sx: { fontSize: "0.8rem", lineHeight: 1.6 } }}
                                sx={{
                                  mt: 1.5, mb: 1,
                                  "& .MuiInputBase-input::placeholder": {
                                    color: generatingReply ? "text.primary" : undefined,
                                    opacity: generatingReply ? 0.7 : undefined,
                                  },
                                }}
                              />
                              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mb: 1.5 }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  disableElevation
                                  startIcon={generatingReply ? <CircularProgress size={12} /> : <Zap size={13} />}
                                  onClick={() => handleGenerateReply(convo.nylas_thread_id)}
                                  disabled={generatingReply}
                                  sx={{
                                    fontSize: "0.7rem",
                                    textTransform: "none",
                                    height: 32,
                                    borderRadius: 0.5,
                                    px: 1.5,
                                    bgcolor: "white",
                                    borderColor: "rgba(0,0,0,0.15)",
                                    color: "text.primary",
                                    overflow: "hidden",
                                    "@keyframes borderShimmer": {
                                      "0%": { borderColor: "rgba(0,0,0,0.15)", boxShadow: "0 0 0 0 rgba(25,118,210,0)" },
                                      "50%": { borderColor: "#1976d2", boxShadow: "0 0 6px 0 rgba(25,118,210,0.3)" },
                                      "100%": { borderColor: "rgba(0,0,0,0.15)", boxShadow: "0 0 0 0 rgba(25,118,210,0)" },
                                    },
                                    animation: "borderShimmer 2s ease-in-out infinite",
                                    "&:hover": { bgcolor: "rgba(25,118,210,0.04)", borderColor: "#1976d2", boxShadow: "0 0 8px 0 rgba(25,118,210,0.25)" },
                                  }}
                                >
                                  Generate Reply
                                </Button>
                                <Button
                                  variant="contained"
                                  size="small"
                                  disableElevation
                                  disabled={(!replyText.trim() || sending) && !justSentReply}
                                  onClick={() => handleReply(convo.nylas_thread_id)}
                                  startIcon={
                                    justSentReply ? <Check size={13} /> : sending ? <CircularProgress size={12} sx={{ color: "white" }} /> : <Send size={13} />
                                  }
                                  sx={{
                                    fontSize: "0.7rem", textTransform: "none", height: 32, borderRadius: 0.5, px: 2,
                                    ...(justSentReply && { bgcolor: "#2e7d32", "&:hover": { bgcolor: "#2e7d32" }, "&.Mui-disabled": { bgcolor: "#2e7d32", color: "white" } }),
                                  }}
                                >
                                  {justSentReply ? "Sent !" : "Send"}
                                </Button>
                              </Box>
                              {/* All messages, newest first */}
                              <Box sx={{ maxHeight: 400, overflowY: "auto" }}>
                                {reversed.map((msg, idx) => (
                                  <Box
                                    key={msg.id}
                                    sx={{
                                      pl: 1.5,
                                      borderLeft: "2px solid #e0e0e0",
                                      mb: idx < reversed.length - 1 ? 1.5 : 0,
                                      fontSize: "0.75rem",
                                      "& img": { maxWidth: "100%" },
                                      "& table": { fontSize: "0.75rem" },
                                    }}
                                  >
                                    <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 0.5 }}>
                                      {msg.status === "sent"
                                        ? "You"
                                        : (msg.sent_by || convo.company_name || "Customer")}
                                      {msg.sent_at && ` · ${timeAgo(msg.sent_at)}`}
                                      {msg.status === "sent" && msg.read_at && (
                                        <span style={{ color: "#4caf50", marginLeft: 6 }}>
                                          Seen {timeAgo(msg.read_at)}
                                        </span>
                                      )}
                                    </Typography>
                                    <Box dangerouslySetInnerHTML={{ __html: msg.message }} />
                                  </Box>
                                ))}
                              </Box>
                            </>
                          );
                        }
                        // SMS: chat bubble layout
                        return (
                          <>
                            <Box sx={{ py: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                              {msgs.map((msg) => {
                                const isSent = msg.status === "sent";
                                return (
                                  <Box
                                    key={msg.id}
                                    sx={{ alignSelf: isSent ? "flex-end" : "flex-start", maxWidth: "80%" }}
                                  >
                                    <Box
                                      sx={{
                                        bgcolor: isSent ? "#1976d2" : "#f5f5f5",
                                        color: isSent ? "white" : "text.primary",
                                        borderRadius: isSent ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
                                        px: 1.5,
                                        py: 1,
                                      }}
                                    >
                                      <Typography variant="body2" sx={{ fontSize: "0.8rem", whiteSpace: "pre-wrap" }}>
                                        {msg.message}
                                      </Typography>
                                    </Box>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: "0.65rem",
                                        color: "text.disabled",
                                        mt: 0.25,
                                        display: "block",
                                        textAlign: isSent ? "right" : "left",
                                      }}
                                    >
                                      {timeAgo(msg.sent_at)}
                                    </Typography>
                                  </Box>
                                );
                              })}
                            </Box>
                            <Box sx={{ mt: 1 }}>
                              <TextField
                                size="small"
                                multiline
                                minRows={6}
                                maxRows={8}
                                placeholder={generatingReply ? (generatingPhrase + "\u258D") : "Type a reply..."}
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                fullWidth
                                InputProps={{ sx: { fontSize: "0.8rem" } }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleReply(convo.nylas_thread_id);
                                  }
                                }}
                              />
                              <Typography variant="caption" sx={{ fontSize: "0.65rem", color: replyText.length > 160 ? "#d32f2f" : "text.secondary", textAlign: "right", mt: 0.5, mb: 0.5, display: "block" }}>
                                {replyText.length} / 160
                              </Typography>
                              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  disableElevation
                                  startIcon={generatingReply ? <CircularProgress size={12} /> : <Zap size={13} />}
                                  onClick={() => handleGenerateReply(convo.nylas_thread_id)}
                                  disabled={generatingReply}
                                  sx={{
                                    fontSize: "0.7rem",
                                    textTransform: "none",
                                    height: 32,
                                    borderRadius: 0.5,
                                    px: 1.5,
                                    bgcolor: "white",
                                    borderColor: "rgba(0,0,0,0.15)",
                                    color: "text.primary",
                                    overflow: "hidden",
                                    "@keyframes borderShimmer": {
                                      "0%": { borderColor: "rgba(0,0,0,0.15)", boxShadow: "0 0 0 0 rgba(25,118,210,0)" },
                                      "50%": { borderColor: "#1976d2", boxShadow: "0 0 6px 0 rgba(25,118,210,0.3)" },
                                      "100%": { borderColor: "rgba(0,0,0,0.15)", boxShadow: "0 0 0 0 rgba(25,118,210,0)" },
                                    },
                                    animation: "borderShimmer 2s ease-in-out infinite",
                                    "&:hover": { bgcolor: "rgba(25,118,210,0.04)", borderColor: "#1976d2", boxShadow: "0 0 8px 0 rgba(25,118,210,0.25)" },
                                  }}
                                >
                                  Generate Reply
                                </Button>
                                <Button
                                  variant="contained"
                                  size="small"
                                  disableElevation
                                  disabled={!replyText.trim() || sending}
                                  onClick={() => handleReply(convo.nylas_thread_id)}
                                  sx={{ fontSize: "0.7rem", textTransform: "none", height: 32, borderRadius: 0.5, px: 2 }}
                                >
                                  {sending ? <CircularProgress size={16} sx={{ color: "white" }} /> : <Send size={16} />}
                                </Button>
                              </Box>
                            </Box>
                          </>
                        );
                      })()}
                    </>
                  )}
                </Box>
              </Collapse>
            </Paper>
          );
        })}
        {hasMore && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleLoadMore}
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={14} /> : null}
              sx={{
                textTransform: "none",
                fontSize: "0.8rem",
                borderRadius: 1,
                px: 3,
                borderColor: "rgba(0,0,0,0.15)",
                color: "text.secondary",
                "&:hover": { borderColor: "rgba(0,0,0,0.3)" },
              }}
            >
              {loadingMore ? "Loading..." : "Load More"}
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <>
      <Helmet title="Datajolt | CRM v2 Inbox" />
      <Box sx={{ display: "flex", alignItems: "center", margin: "12px 8px", height: "45px" }}>
        <h4 style={{ marginBottom: 0 }}>
          <b style={{ color: "#090E47" }}>Inbox</b>
        </h4>
      </Box>
      <hr style={{ margin: "0 8px" }} />
      {grantStatus === "loading" ? (
        <Box sx={{ px: 3, py: 2 }}>
          {Array.from({ length: 8 }).map((_, i) => (
            <Paper key={i} variant="outlined" sx={{ mb: 1, borderRadius: 1.5, px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
              <Skeleton variant="circular" width={14} height={14} />
              <Skeleton variant="circular" width={14} height={14} />
              <Skeleton variant="rounded" width={90} height={18} sx={{ borderRadius: 2 }} />
              <Skeleton variant="text" width="40%" height={20} />
              <Box sx={{ flex: 1 }} />
              <Skeleton variant="text" width={50} height={16} />
            </Paper>
          ))}
        </Box>
      ) : grantStatus === "connected" ? (
        renderConversationList()
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: 1, minHeight: "60vh" }}>
          <Box sx={{ textAlign: "center", maxWidth: 440 }}>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                bgcolor: "rgba(33,150,243,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 3,
              }}
            >
              <Mail size={36} color="#1565c0" />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700, fontSize: "1.5rem", mb: 1 }}>
              Connect Your Email
            </Typography>
            <Typography sx={{ fontSize: "0.9rem", color: "text.secondary", mb: 3, lineHeight: 1.6 }}>
              Link your email inbox to send and receive messages directly from Datajolt.
              All conversations will be tracked and synced with your CRM contacts.
            </Typography>
            <Button
              variant="contained"
              disableElevation
              size="large"
              startIcon={connecting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : <ExternalLink size={18} />}
              onClick={handleConnect}
              disabled={connecting}
              sx={{
                textTransform: "none",
                fontSize: "0.95rem",
                fontWeight: 600,
                borderRadius: 2,
                px: 4,
                py: 1.5,
                bgcolor: "#1d4ed8",
                "&:hover": { bgcolor: "#1e40af" },
              }}
            >
              {connecting ? "Redirecting..." : "Connect Email Inbox"}
            </Button>
            <Typography sx={{ fontSize: "0.75rem", color: "text.disabled", mt: 2 }}>
              Supports Gmail, Outlook, and other email providers via Nylas.
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
};

export default InboxPage;
